const TEXT_FIT_RIGHT_MARGIN = 45;
const MIN_TEXT_HEIGHT = 10;
const scalingTextElements = new WeakSet();

const renderCanvas = (canvas) => {
  if (typeof canvas?.requestRenderAll === "function") {
    canvas.requestRenderAll();
  } else {
    canvas?.renderAll?.();
  }
};

export const scaleTextElementFontSize = (
  canvas,
  textElement,
  { render = true } = {}
) => {
  if (!textElement || scalingTextElements.has(textElement)) return false;

  const { fontSize, height, scaleX, scaleY, width } = textElement;
  if (
    ![fontSize, height, scaleX, scaleY, width].every(Number.isFinite) ||
    height <= 0 ||
    width <= 0 ||
    scaleX <= 0 ||
    scaleY <= 0
  ) {
    return false;
  }

  const normalizedScaleY = Math.max(scaleY, MIN_TEXT_HEIGHT / height);
  const nextFontSize = fontSize * normalizedScaleY;

  scalingTextElements.add(textElement);
  try {
    textElement.set({
      height: height * normalizedScaleY,
      width: width * scaleX,
      scaleX: 1,
      scaleY: 1,
      fontSize: nextFontSize,
    });
    textElement.setCoords?.();

    if (!textElement.dontSetInitialFontSize) {
      textElement.initialFontSize = nextFontSize;
    }
  } finally {
    scalingTextElements.delete(textElement);
  }

  if (render) renderCanvas(canvas);
  return true;
};

export const fitTextElementToCanvasWidth = (
  canvas,
  textElement,
  { rightMargin = TEXT_FIT_RIGHT_MARGIN } = {}
) => {
  if (!canvas || !textElement) return;

  const canvasWidth = canvas.getWidth();
  const textLeft = Number(textElement.left) || 0;
  const availableWidth = canvasWidth - rightMargin - textLeft;

  // An element starting inside the right margin cannot be made to satisfy the
  // margin by scaling. The previous loop kept shrinking past zero forever.
  if (!Number.isFinite(availableWidth) || availableWidth <= 0) return;

  const currentWidth = textElement.getScaledWidth();
  const currentHeight = textElement.getScaledHeight();
  if (!Number.isFinite(currentWidth) || currentWidth <= 0) return;

  const originalCenterY = textElement.top + currentHeight / 2;
  const initialFontSize =
    textElement.scalingSetFontSize || textElement.fontSize;
  let resizeRatio = 1;

  if (currentWidth > availableWidth) {
    resizeRatio = availableWidth / currentWidth;
  } else if (
    Number.isFinite(initialFontSize) &&
    textElement.fontSize > 0 &&
    textElement.fontSize < initialFontSize
  ) {
    resizeRatio = Math.min(
      initialFontSize / textElement.fontSize,
      availableWidth / currentWidth
    );
  }

  if (Number.isFinite(resizeRatio) && resizeRatio > 0 && resizeRatio !== 1) {
    textElement.set({
      scaleX: textElement.scaleX * resizeRatio,
      scaleY: textElement.scaleY * resizeRatio,
    });
    scaleTextElementFontSize(canvas, textElement, { render: false });
  }

  textElement.set({
    top: originalCenterY - textElement.getScaledHeight() / 2,
  });
  textElement.setCoords?.();
  renderCanvas(canvas);
};
